import { DataService } from './data-service';
import { FeedService } from './feed-service';

// NOTE: always use trailing slash otherwise Stage makes an unneccessary redirect.

export class API {
  #testMode = false;

  constructor() {
    this.dataService = new DataService({
      authStoreKey: 'pih-token',
      baseUrl: window.PIH?.apiUrl || '/en/api/v1/'
    });

    this.feedsService = new FeedService({
      baseUrl: '/'
    });
  }

  /**
   * Set test mode (used for API tests)
   *
   * @param {boolean} bool -
   */
  setTestMode(bool) {
    this.dataService.setTestMode(bool);
    this.#testMode = bool;
  }

  getTestMode() {
    return this.#testMode;
  }

  getStageToken() {
    return this.dataService.get('token/');
  }

  getLocalisations(language, namespace) {
    return this.dataService.get(`localisations/${language}/${namespace}/`);
  }

  // USER

  loginApiTestUser(username) {
    return this.dataService.post('login-api-test/', {
      'username': username
    });
  }

  async login(data) {
    const res = await this.dataService.post('login/', data);

    if (res.result && res.result.token) {
      this.dataService.setAuthToken(res.result.token);
    }

    return Promise.resolve(res);
  }

  logout() {
    //TODO
    this.dataService.clearAuthToken();
    return this.dataService.post('logout/');
  }

  forgotPassword(data) {
    return this.dataService.post('forgot-password/', data);
  }

  changePassword(data) {
    return this.dataService.post('change-password/', data);
  }

  updateUser(data) {
    return this.dataService.put('user/update/', data);
  }

  getUserInfo() {
    return this.dataService.get('users/current/');
  }

  getItems(ownerId, params = {}) {
    return this.dataService.get(`items/${ownerId}/`, params);
  }

  getItem(ownerId, itemId) {
    return this.dataService.get(`items/${ownerId}/${itemId}/`);
  }

  saveItem(ownerId, itemId, data) {
    return this.dataService.patch(`items/${ownerId}/${itemId}/`, data);
  }

  getTreadItems(ownerId, params = {}) {
    return this.dataService.get(`tread-items/${ownerId}/`, params);
  }

  getTreadItem(ownerId, itemId) {
    return this.dataService.get(`tread-items/${ownerId}/${itemId}/`);
  }

  saveTreadItem(ownerId, itemId, data) {
    return this.dataService.patch(`tread-items/${ownerId}/${itemId}/`, data);
  }

  getItemFamilies(ownerId, params = {}) {
    return this.dataService.get(`itemfamilies/${ownerId}/`, params);
  }

  createItemFamily(ownerId, data) {
    return this.dataService.post(`itemfamilies/${ownerId}/`, data);
  }

  getItemFamily(ownerId, itemFamilyId) {
    return this.dataService.get(`itemfamilies/${ownerId}/${itemFamilyId}/`);
  }

  deleteItemFamily(ownerId, itemFamilyId) {
    return this.dataService.del(`itemfamilies/${ownerId}/${itemFamilyId}/`);
  }

  //DEPRECATED
  saveItemFamily(ownerId, itemFamilyId, data) {
    return false; //DEPRECATED
  }

  //saves draft version
  saveDraftItemFamily(ownerId, itemFamilyId, data) {
    return this.dataService.put(`itemfamilies/draft/${ownerId}/${itemFamilyId}/`, data);
  }

  //saves publish version
  savePublishedItemFamily(ownerId, itemFamilyId, data) {
    return this.dataService.put(`itemfamilies/published/${ownerId}/${itemFamilyId}/`, data);
  }

  //publish item family (saves draft + publish versions)
  publishItemFamily(ownerId, itemFamilyId, data) {
    return this.dataService.put(`itemfamilies/publish/${ownerId}/${itemFamilyId}/`, data);
  }

  getPins() {
    return this.dataService.get('user/pins/');
  }

  savePins(data) {
    return this.dataService.post('user/pins/', data);
  }

  deletePins(data) {
    return this.dataService.del('user/pins/', data);
  }

  getSavedSearches() {
    return this.dataService.get('user/saved-searches/');
  }

  saveSearches(data) {
    return this.dataService.post('user/saved-searches/', data);
  }

  deleteSearch(data) {
    return this.dataService.del('user/saved-searches/', data);
  }

  //deprecated, use getGlobals
  itemSearchConfig(ownerId) {
    return this.dataService.get(`item-search/${ownerId}/`);
  }

  getGlobals(ownerId, globalsId = '') {
    if(globalsId) {
      return this.dataService.get(`globals/${ownerId}/${globalsId}/`);
    }

    return this.dataService.get(`globals/${ownerId}/`);
  }

  getFeedInfo(ownerId, feedId) {
    return this.dataService.get(`feed/${ownerId}/${feedId}/`);
  }

  saveFeedOrder(ownerId, feedId, data) {
    return this.dataService.put(`feed/${ownerId}/${feedId}/`, data);
  }

  getFeed(ownerId, feedId, language, token) {
    return this.feedsService.get(`${language}/feeds/${feedId}/${ownerId}/`, { token });
  }

  getTyreAwards(ownerId, params = {}) {
    return this.dataService.get(`tyre-awards/${ownerId}/`, params);
  }

  getTyreAward(ownerId, awardId) {
    return this.dataService.get(`tyre-award/${ownerId}/${awardId}/`);
  }

  getFamilyTyreAwards(ownerId, itemFamilyId) {
    return this.dataService.get(`tyre-awards/${ownerId}/${itemFamilyId}/`);
  }

  getTyreAwardMagazines() {
    return this.dataService.get('tyre-awards/magazines/');
  }

  addTyreAward(ownerId, itemFamilyId, data) {
    return this.dataService.put(`tyre-awards/${ownerId}/${itemFamilyId}/`, data);
  }

  updateTyreAward(ownerId, itemFamilyId, data) {
    return this.dataService.patch(`tyre-awards/${ownerId}/${itemFamilyId}/`, data);
  }

  updateTyreAwardTranslation(ownerId, awardId, data) {
    return this.dataService.patch(`tyre-award/${ownerId}/${awardId}/`, data);
  }

  getReviews(ownerId, params = {}) {
    return this.dataService.get(`reviews/${ownerId}/`, params);
  }

  getFamilyReviews(ownerId, itemFamilyId) {
    return this.dataService.get(`reviews/${ownerId}/${itemFamilyId}/`);
  }

  // Wheels

  getWheels(ownerId) {
    return this.dataService.get(`rims/${ownerId}/`);
  }

  getWheel(ownerId, wheelId) {
    return this.dataService.get(`rims/${ownerId}/${wheelId}/`);
  }

  createWheel(ownerId, data) {
    return this.dataService.post(`rims/${ownerId}/`, data);
  }

  saveDraftWheel(ownerId, wheelId, data) {
    return this.dataService.put(`rims/draft/${ownerId}/${wheelId}/`, data);
  }

  savePublishedWheel(ownerId, wheelId, data) {
    return this.dataService.put(`rims/published/${ownerId}/${wheelId}/`, data);
  }

  // Publish (saves draft + publish versions)
  publishWheel(ownerId, wheelId, data) {
    return this.dataService.put(`rims/publish/${ownerId}/${wheelId}/`, data);
  }

  // Deletes draft + publishe versions
  deleteWheel(ownerId, wheelId) {
    return this.dataService.del(`rims/${ownerId}/${wheelId}/`);
  }

  // Treads
  getTreads(ownerId) {
    return this.dataService.get(`treads/${ownerId}/`);
  }

  getTread(ownerId, treadId) {
    return this.dataService.get(`treads/${ownerId}/${treadId}`);
  }

  createTread(ownerId, data) {
    return this.dataService.post(`treads/${ownerId}/`, data);
  }

  deleteTread(ownerId, treadId) {
    return this.dataService.del(`treads/${ownerId}/${treadId}/`);
  }

  saveDraftTread(ownerId, treadId, data) {
    return this.dataService.put(`treads/draft/${ownerId}/${treadId}/`, data);
  }

  savePublishedTread(ownerId, treadId, data) {
    return this.dataService.put(`treads/published/${ownerId}/${treadId}/`, data);
  }

  publishTread(ownerId, treadId, data) {
    return this.dataService.put(`treads/publish/${ownerId}/${treadId}/`, data);
  }

  // Catalog
  requestCatalogGeneration(params) {
    return this.dataService.get('catalog/generate/', params);
  }

  getCatalog(jobId) {
    return this.dataService.get(`catalog/progress/${jobId}/`);
  }

  // Cancels any ongoing request
  cancelRequest() {
    this.dataService.cancelRequest();
  }
}

export default new API();
